import _ from 'lodash'
import { rrulestr, RRule, Options, Weekday } from 'rrule'

export type Frequency = 'none' | 'daily' | 'weekly' | 'monthly' | 'yearly'
export type EndType = 'never' | 'count' | 'until'
export type DaySelectionMode = 'each' | 'onthe'

export interface RecurrenceOptions {
  frequency: Frequency
  interval: number
  endType: EndType
  count: number
  until: string
  byweekday: number[]
  bymonthday: number[]
  bymonth: number[]
  bysetpos?: number
  monthlyMode: DaySelectionMode
  yearlyMode: DaySelectionMode
}

export const FREQUENCY_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
]

export const END_TYPE_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: 'count', label: 'After' },
  { value: 'until', label: 'On date' },
]

export const POSITION_OPTIONS = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: 5, label: 'Fifth' },
  { value: -2, label: 'Next to last' },
  { value: -1, label: 'Last' },
]

export const WEEKDAY_OPTIONS = [
  { value: RRule.SU.weekday, label: 'Sun' },
  { value: RRule.MO.weekday, label: 'Mon' },
  { value: RRule.TU.weekday, label: 'Tue' },
  { value: RRule.WE.weekday, label: 'Wed' },
  { value: RRule.TH.weekday, label: 'Thu' },
  { value: RRule.FR.weekday, label: 'Fri' },
  { value: RRule.SA.weekday, label: 'Sat' },
]

export const DAY_TYPE_OPTIONS = [
  { value: 'sunday', label: 'Sunday', weekdays: [RRule.SU.weekday] },
  { value: 'monday', label: 'Monday', weekdays: [RRule.MO.weekday] },
  { value: 'tuesday', label: 'Tuesday', weekdays: [RRule.TU.weekday] },
  { value: 'wednesday', label: 'Wednesday', weekdays: [RRule.WE.weekday] },
  { value: 'thursday', label: 'Thursday', weekdays: [RRule.TH.weekday] },
  { value: 'friday', label: 'Friday', weekdays: [RRule.FR.weekday] },
  { value: 'saturday', label: 'Saturday', weekdays: [RRule.SA.weekday] },
  {
    value: 'day',
    label: 'Day',
    weekdays: [
      RRule.MO.weekday,
      RRule.TU.weekday,
      RRule.WE.weekday,
      RRule.TH.weekday,
      RRule.FR.weekday,
      RRule.SA.weekday,
      RRule.SU.weekday,
    ],
  },
  {
    value: 'weekday',
    label: 'Weekday',
    weekdays: [
      RRule.MO.weekday,
      RRule.TU.weekday,
      RRule.WE.weekday,
      RRule.TH.weekday,
      RRule.FR.weekday,
    ],
  },
  { value: 'weekend', label: 'Weekend day', weekdays: [RRule.SA.weekday, RRule.SU.weekday] },
]

export const MONTH_OPTIONS = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Feb' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' },
  { value: 5, label: 'May' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Aug' },
  { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dec' },
]

const RRULE_FREQ_TO_NAME: Record<number, Frequency> = {
  [RRule.DAILY]: 'daily',
  [RRule.WEEKLY]: 'weekly',
  [RRule.MONTHLY]: 'monthly',
  [RRule.YEARLY]: 'yearly',
}

const NAME_TO_RRULE_FREQ: Record<Exclude<Frequency, 'none'>, number> = {
  daily: RRule.DAILY,
  weekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
  yearly: RRule.YEARLY,
}

export function getTodayWeekday(): number {
  const day = new Date().getDay()
  return day === 0 ? RRule.SU.weekday : day - 1
}

export function getCurrentMonth(): number {
  return new Date().getMonth() + 1
}

export function getTodayDayOfMonth(): number {
  return new Date().getDate()
}

export function parseRRuleString(rruleString: string): RecurrenceOptions | null {
  if (!rruleString) {
    return null
  }

  try {
    const opts = rrulestr(rruleString).options
    const frequency = RRULE_FREQ_TO_NAME[opts.freq] ?? 'daily'
    const bysetpos = firstOrUndefined(opts.bysetpos)
    const hasBysetpos = bysetpos !== undefined

    return {
      frequency,
      interval: opts.interval || 1,
      endType: opts.count ? 'count' : opts.until ? 'until' : 'never',
      count: opts.count || 10,
      until: opts.until ? opts.until.toISOString().split('T')[0] : '',
      byweekday: toNumberArray(opts.byweekday),
      bymonthday: toNumberArray(opts.bymonthday),
      bymonth: toNumberArray(opts.bymonth),
      bysetpos,
      monthlyMode: frequency === 'monthly' && hasBysetpos ? 'onthe' : 'each',
      yearlyMode: frequency === 'yearly' && hasBysetpos ? 'onthe' : 'each',
    }
  } catch (e) {
    console.warn('Failed to parse RRULE string:', rruleString, e)
    return null
  }
}

export function generateRRuleString(opts: RecurrenceOptions, dtstart?: string): string | null {
  const { frequency, monthlyMode, yearlyMode } = opts

  if (frequency === 'none') {
    return null
  }

  const options: Partial<Options> = {
    freq: NAME_TO_RRULE_FREQ[frequency],
    interval: opts.interval,
  }

  if (dtstart) {
    options.dtstart = new Date(dtstart)
  }

  if (opts.endType === 'count') {
    options.count = opts.count
  } else if (opts.endType === 'until' && opts.until) {
    options.until = new Date(opts.until)
  }

  if (frequency === 'weekly') {
    setIfPresent(options, 'byweekday', opts)
  } else if (frequency === 'monthly') {
    if (monthlyMode === 'each') {
      setIfPresent(options, 'bymonthday', opts)
    } else {
      setIfPresent(options, 'byweekday', opts)
      setIfPresent(options, 'bysetpos', opts)
    }
  } else if (frequency === 'yearly') {
    setIfPresent(options, 'bymonth', opts)
    if (yearlyMode === 'each') {
      setIfPresent(options, 'bymonthday', opts)
    } else {
      setIfPresent(options, 'byweekday', opts)
      setIfPresent(options, 'bysetpos', opts)
    }
  }

  const result = new RRule(options).toString()

  // since the server uses iCal4j's `Recur` value processor instead of RRule wrapper, we need to
  // strip off the `RRULE:` prefix here. See
  // https://github.com/ical4j/ical4j/blob/develop/src/main/java/net/fortuna/ical4j/model/Recur.java
  //
  // NOTE: this might not handle the presence of dtstart well, but we're not currently setting that
  // in recurrence-input anyway since our server will use a different field on the appointment for
  // that. If we end up needing to support DTSTART here, the server will likely need changes to
  // better handle the presence of `DTSTART:` and `RRULE:` line prefixes.
  return result.replace(/^RRULE:/, '')
}

function setIfPresent<K extends keyof Options>(
  target: Partial<Options>,
  key: K,
  source: any
): void {
  const value = source[key]
  if (value === undefined || (Array.isArray(value) && value.length === 0)) {
    return
  }
  target[key] = value
}

type WeekdayOrNumber = Weekday | number

function toNumberArray(value: WeekdayOrNumber | WeekdayOrNumber[] | null | undefined): number[] {
  if (value === undefined || value === null) {
    return []
  }

  const arr = Array.isArray(value) ? value : [value]

  return arr.map((v) => {
    if (typeof v === 'object' && 'weekday' in v) {
      return v.weekday
    }
    return v
  })
}

function firstOrUndefined(value: number | number[] | null | undefined): number | undefined {
  if (value === undefined || value === null) {
    return undefined
  } else if (Array.isArray(value)) {
    return value[0]
  }
  return value
}
